import {
  Box,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Button,
  Drawer,
  Input,
  SelectField,
  CalendarIcon,
  DollarCashIcon,
  LocationPinIcon,
  ClockIcon,
  FolderIcon,
  InvoiceIcon,
} from "components";
import {
  useGetActiveResourceCount,
  useGetAllCountries,
  useGetAllStateByCountryId,
  useGlobalStore,
  useSubscriptionDetails,
  useUpdateMspBilling,
} from "controller";
import React, { useEffect, useState } from "react";
import { dayjs } from "utilz";

import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";

import { PayNowCard } from "../create-subscription/PayNowCard";
import { PaymentDetailsCard } from "./PaymentDetailsCard";
import { CancelSubscriptionFeedbackDrawer } from "./cancel-subsription-feedback";

const schema = yup.object({
  billingCountry: yup.number().required("Billing country is required"),
  billingState: yup.number().nullable(),
  billingPostalCode: yup
    .string()
    .nullable()
    .max(10, "Zip code must be at most 10 characters"),
  billingAddressLine1: yup
    .string()
    .required("Billing address is required")
    .max(150, "Address line 1 is too long"),
  billingAddressLine2: yup
    .string()
    .nullable()
    .max(150, "Address line 2 is too long"),
});

export const OverviewTab02 = () => {
  const theme = useTheme();
  const subscriptionStatus = useGlobalStore(
    (state) => state.subscriptionStatus,
  );
  const currentAccessBlockDate = useGlobalStore(
    (state) => state.currentAccessBlockDate,
  );
  const dateFormat = useGlobalStore((state) => state.dateFormat);
  const mspId = useGlobalStore((state) => state.mspId);

  // Modal/Drawer states
  const [isManageDrawerOpen, setIsManageDrawerOpen] = useState(false);
  const [isCancelSubscriptionModalOpen, setIsCancelSubscriptionModalOpen] =
    useState(false);
  const [isAddressEditDrawerOpen, setIsAddressEditDrawerOpen] = useState(false);

  // API hooks
  const {
    data: subscriptionDetails,
    refetch: refetchSubscriptionDetails,
    isLoading: isSubsLoading,
  } = useSubscriptionDetails();
  const { data: activeResourceCount } = useGetActiveResourceCount();
  const { data: countries } = useGetAllCountries();
  const { mutateAsync: updateBilling, isLoading: isUpdatingBilling } =
    useUpdateMspBilling();

  // Form setup
  const {
    handleSubmit,
    control,
    reset,
    setValue: setFormValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      billingCountry: 0,
      billingState: 0,
      billingPostalCode: "",
      billingAddressLine1: "",
      billingAddressLine2: "",
    },
    resolver: yupResolver(schema),
  });

  const countryCode = useWatch({ control, name: "billingCountry" });
  const { data: states } = useGetAllStateByCountryId(`${countryCode}` || "");

  // Populate form when subscription details load
  useEffect(() => {
    if (subscriptionDetails) {
      setFormValue("billingCountry", subscriptionDetails.billing_country);
      setFormValue("billingState", subscriptionDetails.billing_state);
      setFormValue(
        "billingPostalCode",
        subscriptionDetails.billing_postal_code,
      );
      setFormValue("billingAddressLine1", subscriptionDetails.line1);
      setFormValue("billingAddressLine2", subscriptionDetails.line2);
    }
  }, [subscriptionDetails, setFormValue]);

  const onSubmitHandler = handleSubmit(
    async ({
      billingAddressLine1,
      billingAddressLine2,
      billingCountry,
      billingPostalCode,
      billingState,
    }) => {
      await updateBilling({
        mspId: mspId || "",
        data: {
          billingAddressLine1,
          billingAddressLine2,
          billingCountry,
          billingZipCode: billingPostalCode,
          billingState,
        },
      });
      toast.success("Billing address updated successfully");
      reset(undefined, { keepValues: true, keepDirty: false });
      refetchSubscriptionDetails();
      setIsAddressEditDrawerOpen(false);
    },
  );

  // Calculate prices
  const pricePerSeat = subscriptionDetails?.price
    ? (subscriptionDetails.price / 100) *
      ((100 - (subscriptionDetails.percent_off || 0)) / 100)
    : 0;

  const totalBillingAmount = subscriptionDetails
    ? Number(subscriptionDetails.seat_quantity) * pricePerSeat
    : 0;

  // Get country and state names for display
  const getCountryName = () => {
    if (!countries || !subscriptionDetails?.billing_country) return "";
    const country = countries.find(
      (c: any) => c.id === subscriptionDetails.billing_country,
    );
    return country?.name || "";
  };

  const getStateName = () => {
    if (!states || !subscriptionDetails?.billing_state) return "";
    const state = states.find(
      (s: any) => s.id === subscriptionDetails.billing_state,
    );
    return state?.state_name || "";
  };

  const getFullAddress = () => {
    const parts = [
      subscriptionDetails?.line1,
      subscriptionDetails?.line2,
      getStateName(),
      getCountryName(),
      subscriptionDetails?.billing_postal_code,
    ].filter(Boolean);
    return parts.join(", ");
  };

  if (isSubsLoading) {
    return (
      <Box sx={{ p: "24px" }}>
        <Skeleton variant="rectangular" height={200} sx={{ mb: "16px" }} />
        <Skeleton variant="rectangular" height={150} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        padding: "32px",
        backgroundColor: "#fff",
        minHeight: "100vh",
        width: "100%",
      }}
    >
      {/* Billing Section Header */}
      <Stack direction="row" gap="4px" alignItems="center">
        <InvoiceIcon />
        <Typography variant="h6">Billing</Typography>
      </Stack>
      <Typography
        mt="8px"
        variant="body-medium"
        color={theme.palette.lightBg.low}
      >
        Information about your current plan.
      </Typography>

      {/* Main Grid - Plan Card and Billing Info */}
      <Grid container mt="16px" spacing="24px" mb="32px">
        {/* Left Column - Active Plan Card */}
        <Grid item xs={12} sm={8}>
          <Paper
            variant="outlined"
            sx={{
              p: "24px",
              borderRadius: "8px",
              borderColor: theme.palette.neutral["015"],
              backgroundColor: theme.palette.neutral.main,
            }}
          >
            {/* Plan Header */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb="16px"
            >
              <Chip
                label="ACTIVE PLAN"
                size="small"
                sx={{
                  ...theme.typography["tag-label-xs"],
                  backgroundColor: theme.palette.primitive.system.violet[50],
                  color: theme.palette.primitive.system.violet[800],
                  fontWeight: "bold",
                  borderRadius: "4px",
                }}
              />
              {/* {!subscriptionDetails?.is_custom_plan && (
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => setIsManageDrawerOpen(true)}
                >
                  Change Plan
                </Button>
              )} */}
            </Stack>

            {/* Plan Name */}
            <Typography variant="h5">
              {subscriptionDetails?.plan_name}
            </Typography>

            {/* Progress Bar */}
            <LinearProgress
              variant="determinate"
              value={
                subscriptionDetails?.seat_quantity
                  ? (activeResourceCount / subscriptionDetails.seat_quantity) *
                    100
                  : 0
              }
              sx={{
                mt: "16px",
                height: 8,
                borderRadius: 4,
                backgroundColor: theme.palette.neutral["015"],
                "& .MuiLinearProgress-bar": {
                  backgroundColor: theme.palette.success.main,
                },
              }}
            />

            {/* Technicians Count */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="body-medium"
                color={theme.palette.lightBg.high}
              >
                {activeResourceCount} of {subscriptionDetails?.seat_quantity}{" "}
                Active technicians
              </Typography>
              <Button
                variant="ghost"
                size="small"
                onClick={() => setIsManageDrawerOpen(true)}
              >
                Manage technicians
              </Button>
            </Stack>

            {/* Features Box */}
            <Stack
              sx={{
                mt: "16px",
                backgroundColor: theme.palette.neutral["005"],
                p: "12px",
                borderRadius: "8px",
                gap: "16px",
              }}
            >
              <Typography variant="body-medium">
                Features shipped with the plan :
              </Typography>
              <Stack direction="row" spacing="32px">
                <Stack alignItems="center" spacing="8px">
                  <ClockIcon />
                  <Typography variant="caption-2">Timesheet</Typography>
                </Stack>
                <Stack alignItems="center" spacing="8px">
                  <FolderIcon />
                  <Typography variant="caption-2">Projects</Typography>
                </Stack>
                <Stack alignItems="center" spacing="8px">
                  <InvoiceIcon />
                  <Typography variant="caption-2">Billing</Typography>
                </Stack>
              </Stack>
            </Stack>

            {/* Cancel Subscription Button */}
            <Button
              sx={{ mt: "16px" }}
              variant="secondary"
              onClick={() => setIsCancelSubscriptionModalOpen(true)}
            >
              Cancel subscription
            </Button>
          </Paper>
        </Grid>

        {/* Right Column - Billing Info Cards */}
        <Grid item xs={12} sm={4}>
          <Stack spacing="24px">
            {/* Next Billing Date Card */}
            <Paper
              variant="outlined"
              sx={{
                p: "24px",
                borderRadius: "12px",
                borderColor: theme.palette.neutral["015"],
                backgroundColor: theme.palette.neutral.main,
              }}
            >
              <Stack
                direction="row"
                spacing="8px"
                alignItems="center"
                mb="16px"
              >
                <CalendarIcon sx={{ color: theme.palette.lightBg.low }} />
                <Typography
                  variant="body-medium"
                  color={theme.palette.lightBg.medium}
                >
                  Next billing date
                </Typography>
              </Stack>
              {subscriptionStatus !== "past_due" ? (
                <>
                  <Typography variant="h3" mb="8px">
                    {dayjs(subscriptionDetails?.current_period_end).format(
                      "MMMM DD YYYY",
                    )}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={theme.palette.lightBg.medium}
                  >
                    *${pricePerSeat.toFixed(2)} USD per seat
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="h6" color={theme.palette.error.main}>
                    Payment unsuccessful
                  </Typography>
                  <Typography
                    variant="body-small"
                    color={theme.palette.lightBg.medium}
                  >
                    Next retry:{" "}
                    {dayjs(subscriptionDetails?.next_payment_attempt).format(
                      "MMMM DD YYYY",
                    )}
                  </Typography>
                </>
              )}
            </Paper>

            {/* Total Billing Amount Card */}
            <Paper
              variant="outlined"
              sx={{
                p: "24px",
                borderRadius: "12px",
                borderColor: theme.palette.neutral["015"],
                backgroundColor: theme.palette.neutral.main,
              }}
            >
              <Stack
                direction="row"
                spacing="8px"
                alignItems="center"
                mb="16px"
              >
                <DollarCashIcon sx={{ color: theme.palette.lightBg.low }} />
                <Typography
                  variant="body-medium"
                  color={theme.palette.lightBg.medium}
                >
                  Total billing amount
                </Typography>
              </Stack>
              <Typography variant="h3" mb="8px">
                ${totalBillingAmount.toFixed(2)}
              </Typography>
              <Typography
                variant="caption"
                color={theme.palette.lightBg.medium}
              >
                *currency in USD
              </Typography>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      {/* Bottom Grid - Address and Payment */}
      <Grid container spacing="24px">
        {/* Customer Address Details */}
        <Grid item xs={12} md={6}>
          <Box>
            <Stack direction="row" spacing="8px" alignItems="center" mb="8px">
              <LocationPinIcon fontSize="small" />
              <Typography variant="h6">Customer billing address</Typography>
            </Stack>
            <Typography
              variant="body-medium"
              color={theme.palette.lightBg.medium}
              mb="16px"
            >
              View, add and edit information about your address details
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: "16px",
                borderRadius: "8px",
                backgroundColor: theme.palette.neutral["005"],
                borderColor: theme.palette.neutral["015"],
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", mb: 0.5 }}
                  >
                    {subscriptionDetails?.company_name || "Company name"}
                  </Typography>
                  <Typography
                    variant="body-small"
                    color={theme.palette.lightBg.medium}
                  >
                    {getFullAddress() || "No address on file"}
                  </Typography>
                </Box>
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => setIsAddressEditDrawerOpen(true)}
                >
                  Edit
                </Button>
              </Stack>
            </Paper>
          </Box>
        </Grid>

        {/* Payment Method */}
        <Grid item xs={12} md={6}>
          <PaymentDetailsCard defaultCard={subscriptionDetails?.source || ""} />
        </Grid>
      </Grid>

      {/* Cancel Subscription Drawer */}
      {isCancelSubscriptionModalOpen && (
        <CancelSubscriptionFeedbackDrawer
          currentAccessBlockDate={dayjs(currentAccessBlockDate).format(
            dateFormat,
          )}
          onClose={() => setIsCancelSubscriptionModalOpen(false)}
          open={isCancelSubscriptionModalOpen}
          onConfirmClick={() => {
            refetchSubscriptionDetails();
            window.location.reload();
          }}
        />
      )}

      {/* Manage Seats Drawer */}
      {isManageDrawerOpen && (
        <Drawer
          open={isManageDrawerOpen}
          title="Manage"
          footer={<></>}
          onClose={() => setIsManageDrawerOpen(false)}
        >
          <Stack sx={{ height: "100%", width: "614px" }}>
            <PayNowCard
              type="EDIT"
              data={{
                promocode: subscriptionDetails?.promotion_code,
                seatQuantity: subscriptionDetails?.seat_quantity,
                discountPercentage: subscriptionDetails?.percent_off || "0",
                price: subscriptionDetails?.price,
                discountId: subscriptionDetails?.discount,
                planId: subscriptionDetails?.plan_id,
                recurringInterval: subscriptionDetails?.recurring_interval,
              }}
              onClose={() => setIsManageDrawerOpen(false)}
            />
          </Stack>
        </Drawer>
      )}

      {/* Address Edit Drawer */}
      {isAddressEditDrawerOpen && (
        <Drawer
          open={isAddressEditDrawerOpen}
          title="Edit billing address"
          footer={
            <Stack
              direction="row"
              spacing="16px"
              justifyContent="flex-end"
              p="16px"
            >
              <Button
                variant="secondary"
                onClick={() => setIsAddressEditDrawerOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={onSubmitHandler}
                loading={isUpdatingBilling}
              >
                Save
              </Button>
            </Stack>
          }
          onClose={() => setIsAddressEditDrawerOpen(false)}
        >
          <Stack sx={{ width: "500px", p: "24px", gap: "20px" }}>
            <Input
              required
              name="billingAddressLine1"
              label="Address line 1"
              control={control}
              error={!!errors.billingAddressLine1}
              errorMessage={errors.billingAddressLine1?.message}
              inputProps={{ maxLength: 150 }}
            />
            <Input
              name="billingAddressLine2"
              label="Address line 2"
              control={control}
              error={!!errors.billingAddressLine2}
              inputProps={{ maxLength: 150 }}
            />
            <SelectField
              required
              options={countries}
              labelMappingKey="name"
              valueMappingKey="id"
              name="billingCountry"
              label="Country"
              isSearchable
              keepMenuItemOriginalCasing
              control={control}
              error={!!errors.billingCountry}
              errorMessage={errors.billingCountry?.message}
            />
            <SelectField
              options={states}
              labelMappingKey="state_name"
              valueMappingKey="id"
              name="billingState"
              label="State"
              isSearchable
              keepMenuItemOriginalCasing
              control={control}
              error={!!errors.billingState}
            />
            <Input
              name="billingPostalCode"
              label="Zip code"
              control={control}
              error={!!errors.billingPostalCode}
              errorMessage={errors.billingPostalCode?.message}
            />
          </Stack>
        </Drawer>
      )}
    </Box>
  );
};
